import { IAccountRespSchema } from '@monetize/types/app';
import { FC } from 'react';
import {
  KeyValueCallout,
  MBox,
  MDivider,
  MFlex,
  MGrid,
  MPageLoader,
} from '../../../components/Monetize';
import { useShowPastDueFields } from '../../../hooks';
import { useOverviewData } from '../../../hooks/AccountDetail';
import { useAccountPastDueInformation } from '../../../hooks/AccountDetail/useAccountPastDueInformation';
import { getDaysOutstanding } from '../../../utils/invoices';
import SubscriptionOverviewTable from './SubscriptionOverviewTable';

type AccountOverviewProps = {
  account: IAccountRespSchema;
  billGroupId?: string;
};

const AccountOverview: FC<AccountOverviewProps> = ({
  account,
  billGroupId,
}: AccountOverviewProps) => {
  const showPastDue = useShowPastDueFields();
  const { id: accountId, defaultCurrency: currency } = account;
  const { listData, accountOverviewStats, loadingAll, loadingOverview } =
    useOverviewData(accountId, billGroupId);
  const { getDueAmountByBillGroupId } = useAccountPastDueInformation(
    accountId,
    {
      enabled: showPastDue,
    },
  );

  const pastDueAmount = account.pastDueAmountOutstanding ?? 0;
  const pastDueInvoiceCount = account.pastDueInvoiceCount ?? 0;
  const amountOutstanding = account.amountOutstanding ?? 0;
  const daysOutstanding = getDaysOutstanding(account.minimumInvoiceDueDate);

  return (
    <MBox data-testid="account-overview-list" overflow="hidden">
      <MGrid
        justifyItems="start"
        gridTemplateColumns="repeat(5, 1fr 0.1fr)"
        columnGap={2}
      >
        <KeyValueCallout
          testIdRoot="account-overview"
          isLoading={loadingAll}
          type="currency"
          label="Recent Invoice"
          value={accountOverviewStats?.recentInvoiceAmount}
          currency={currency}
          verticalDividerAfter
        />
        <KeyValueCallout
          testIdRoot="account-overview"
          isLoading={loadingAll}
          type="currency"
          label="ARR"
          value={accountOverviewStats?.arr}
          currency={currency}
          verticalDividerAfter
        />
        <KeyValueCallout
          testIdRoot="account-overview"
          isLoading={loadingAll}
          type="currency"
          label="MRR"
          value={accountOverviewStats?.mrr}
          currency={currency}
          verticalDividerAfter
        />
        <KeyValueCallout
          testIdRoot="account-overview"
          isLoading={loadingAll}
          type="currency"
          label="Lifetime Value"
          value={accountOverviewStats?.lifetimeValue}
          currency={currency}
          verticalDividerAfter
        />
        <KeyValueCallout
          testIdRoot="account-overview"
          type="text"
          label="Currency"
          value={account?.defaultCurrency}
        />
      </MGrid>
      {showPastDue && (
        <MGrid
          justifyItems="start"
          gridTemplateColumns="repeat(5, 1fr 0.1fr)"
          columnGap={2}
          mt={5}
        >
          <KeyValueCallout
            testIdRoot="account-overview"
            isLoading={loadingAll}
            type="currency"
            label="Amount Outstanding"
            value={amountOutstanding}
            currency={currency}
            verticalDividerAfter
          />
          <KeyValueCallout
            testIdRoot="account-overview"
            isLoading={loadingAll}
            type="currency"
            label="Past Due Amount"
            value={pastDueAmount}
            currency={currency}
            valueColor={pastDueAmount > 0 ? 'tRed.base' : undefined}
            verticalDividerAfter
          />
          <KeyValueCallout
            testIdRoot="account-overview"
            isLoading={loadingAll}
            type="number"
            label="Past Due Invoices"
            value={pastDueInvoiceCount}
            valueColor={pastDueInvoiceCount > 0 ? 'tRed.base' : undefined}
            verticalDividerAfter
          />
          <KeyValueCallout
            testIdRoot="account-overview"
            isLoading={loadingAll}
            type="number"
            label="Days Outstanding"
            value={daysOutstanding}
            valueColor={daysOutstanding > 0 ? 'tRed.base' : undefined}
          />
        </MGrid>
      )}
      <MDivider mt={5} variant="thick" />
      {loadingOverview ? (
        <MPageLoader mt={20} height="auto" />
      ) : (
        <MFlex flexDirection="column">
          {listData.map((data) => (
            <SubscriptionOverviewTable
              key={data.billgroup.id}
              data={data}
              account={account}
              pastDueAmount={
                showPastDue ? getDueAmountByBillGroupId(data.billgroup.id) : 0
              }
            />
          ))}
        </MFlex>
      )}
    </MBox>
  );
};

export default AccountOverview;
